/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, ReactNode, useContext, useEffect, useReducer, useState } from 'react';
import { DriveTabs } from '@common/constants/logbook';
import { useMonthPeriod } from '@common/hooks/logbook';
import { SortingDirections } from '@common/interfaces/LogbookRepository';
import { PerPageType } from '@pages/LogbookPage/components/Pagination';

const [initialFirsDate] = useMonthPeriod(new Date().getFullYear(), new Date().getMonth());
const [, initialLastDate] = useMonthPeriod(new Date().getFullYear(), new Date().getMonth());

type TableState = {
  order: SortingDirections;
  perPage: PerPageType;
  selectedPage: number;
  paginationLimits: { left: number; right: number };
};

type TableAction =
  | { type: 'order'; payload: SortingDirections }
  | { type: 'perPage'; payload: PerPageType }
  | { type: 'selectedPage'; payload: number }
  | { type: 'paginationLimits'; payload: { left: number; right: number } }
  | { type: 'initial'; payload?: DriveTabs };

const initialTableState: TableState = {
  order: 'desc',
  perPage: 5,
  selectedPage: 1,
  paginationLimits: { left: 0, right: 9 },
};

function tableReducer(state: TableState, action: TableAction): TableState {
  switch (action.type) {
    case 'order':
      return { ...state, order: action.payload };
    case 'perPage':
      return { ...state, perPage: action.payload, selectedPage: 1 };
    case 'selectedPage':
      return { ...state, selectedPage: action.payload };
    case 'paginationLimits':
      return { ...state, paginationLimits: action.payload };
    case 'initial': {
      if (!action.payload) {
        return initialTableState;
      }
      return {
        ...initialTableState,
        order: action.payload === DriveTabs.toDo ? 'asc' : 'desc',
      };
    }
    default:
      return state;
  }
}

const initialState = {
  selectedTab: DriveTabs.done,
  tabsNumbers: [0, 0],
  isCarpooled: false,
  datePeriod: { first: initialFirsDate, last: initialLastDate },
  tableState: initialTableState,
  setTabNumbers: (_: [number, number]) => {},
  setSelectedTab: (_: DriveTabs) => {},
  setDatePeriod: (_: { first: Date; last: Date }) => {},
  tableDispatch: (_: TableAction) => {},
  setIsCarpooled: (_: boolean) => {},
};

const LogbookContext = createContext(initialState);

function LogbookProvider({ children }: { children: ReactNode }) {
  const [tabsNumbers, setTabNumbers] = useState([0, 0]);
  const [selectedTab, setSelectedTab] = useState<DriveTabs>(DriveTabs.done);
  const [datePeriod, setDatePeriod] = useState<{ first: Date; last: Date }>({
    first: initialFirsDate,
    last: initialLastDate,
  });

  const [isCarpooled, setIsCarpooled] = useState<boolean>(false);

  const [tableState, tableDispatch] = useReducer(tableReducer, initialTableState);

  useEffect(() => {
    tableDispatch({ type: 'initial', payload: selectedTab });
  }, [datePeriod, selectedTab]);

  return (
    <LogbookContext.Provider
      value={{
        selectedTab,
        setSelectedTab,
        setTabNumbers,
        tabsNumbers,
        datePeriod,
        setDatePeriod,
        tableState,
        tableDispatch,
        isCarpooled,
        setIsCarpooled,
      }}
    >
      {children}
    </LogbookContext.Provider>
  );
}

const useLogbookContext = () => useContext(LogbookContext);

export { LogbookProvider, useLogbookContext };
